import { type ReactNode } from 'react';
import { Header } from './Header';
import { Sidebar, MobileNavDrawer } from './Sidebar';
import { OfflineBanner } from './OfflineBanner';
import { ViewerBanner } from './ViewerBanner';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { CommandPalette } from '@/components/command/CommandPalette';
import { WhatsNewModal } from '@/components/WhatsNewModal';

interface AppShellProps {
  children: ReactNode;
}

/**
 * Main layout: sticky header + persistent sidebar + scrollable content area.
 *
 * The content area is wrapped in an ErrorBoundary so that a render error
 * in one page doesn't blank the whole app — the user sees a friendly
 * error card and can retry or navigate home.
 */
export function AppShell({ children }: AppShellProps) {
  return (
    <div className="h-full flex flex-col bg-surface-canvas">
      <OfflineBanner />
      <ViewerBanner />
      <Header />
      <div className="flex-1 flex overflow-hidden">
        <Sidebar />
        <main className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6 bg-surface-canvas pb-safe-bottom">
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
      </div>
      {/* Phone-only slide-in navigation; no-op on lg+ */}
      <MobileNavDrawer />
      {/* Global ⌘K / Ctrl+K command palette */}
      <CommandPalette />
      {/* One-time "What's New" notice after an update */}
      <WhatsNewModal />
    </div>
  );
}
